/* eslint-disable @typescript-eslint/naming-convention */
import { Component, HostListener, OnInit } from "@angular/core";
import { MatButtonToggleChange } from "@angular/material/button-toggle";
import { MatSelectChange } from "@angular/material/select";
import { DomSanitizer } from "@angular/platform-browser";
import { animate, state, style, transition, trigger } from "@angular/animations";

import { Chart, getOptions, setOptions } from "highcharts";
import { Subject } from "rxjs";
import { filter, first, map } from "rxjs/operators";
import { dffShowHide } from "@dffedb/highcharts";
import { DffHighchartsExtras, DffHighchartsLoadingObserver, dffImportHighcharts } from "@dffedb/highcharts";
import { DffSimpelDialog } from "@dffedb/ui";
import { addDays, addYears, DeviceService, formatDate, formatDayInterval, formatNumber, formatYearInterval, trim, upperCaseFirstChar } from "@dffedb/util";
import { SessionStorage } from "@dffedb/util";
import { AktuelForbrugerOplysningerService } from "@e-forsyning/common/forbruger-oplysninger";
import { AktuelInstallationKeyObserver } from "@e-forsyning/common/installation";

import { MitForbrugSendFejlDialog } from "./mit-forbrug-send-fejl-dialog/mit-forbrug-send-fejl-dialog";
import { Tile } from "./ng-services/mit-forbrug-indstillinger.model";
import { MitForbrugViewModel } from "./ng-services/mit-forbrug.view-model";
import { ForbrugChartPoint, NyForbrugUtils } from "./ng-services/ny-forbrug.utils";
import { NyMitForbrugService } from "./ng-services/ny-mit-forbrug.service";

@Component({
    templateUrl: "./forbrug.component.html",
    styleUrls: ["./forbrug.component.scss"],
    animations: [
        trigger("myInsertRemoveTrigger", [
            transition(":enter", [style({ opacity: 0 }), animate("300ms", style({ opacity: 1, height: 48 }))]),
            transition(":leave", [animate("300ms", style({ opacity: 0, height: 0 }))])
        ])
    ],
    // Vi tilføjer services som providers på komponentniveau.
    // Det bevirker at de oprettes på ny hver gang vi kommer til dette menupunkt.
    // Caches i disse services bliver således cleared når man skifter mellem menupunkter, hvilket betyder
    // at vi får indlæst friske data efter man har indrapporteret måleraflæsninger via menupunktet "Mine aflæsninger".
    providers: [NyForbrugUtils, NyMitForbrugService]
})
export class ForbrugComponent implements OnInit {
    public initObservable = new Subject<void>();
    public vm: MitForbrugViewModel;

    constructor(
        private readonly domSanitizer: DomSanitizer,
        private readonly forbrugService: NyMitForbrugService,
        private readonly aktuelInstallationKeyObserver: AktuelInstallationKeyObserver,
        private readonly aktuelForbrugerService: AktuelForbrugerOplysningerService,
        private readonly sessionStorage: SessionStorage,
        private readonly deviceDetector: DeviceService,
        private readonly forbrugUtils: NyForbrugUtils,
        public readonly sendFejlDialog: MitForbrugSendFejlDialog,
        private readonly simpelDialog: DffSimpelDialog,
        private readonly highchartsLoadingObserver: DffHighchartsLoadingObserver
    ) {}

    public async ngOnInit(): Promise<void> {
        try {
            await this.doNgOnInit();
            this.initObservable.next(null);
        } catch (error) {
            this.initObservable.error(error);
        }
    }

    public async doNgOnInit(): Promise<void> {
        await dffImportHighcharts(
            DffHighchartsExtras.more,
            DffHighchartsExtras.bullet,
            DffHighchartsExtras.drilldown,
            DffHighchartsExtras.exporting,
            DffHighchartsExtras.noDataToDisplay,
            DffHighchartsExtras.accessibility
        );

        const indstillinger = await this.forbrugService.hentIndstillinger().pipe(first()).toPromise();

        const aktuelBruger = await this.sessionStorage.select("user").pipe(first()).toPromise();

        const installationCredentials = await this.aktuelInstallationKeyObserver.valueChanges.pipe(first()).toPromise();

        const aktuelForbruger = await this.aktuelForbrugerService.select().toPromise();

        this.vm = {};
        this.vm.forbrugsgrafLoading$ = this.highchartsLoadingObserver.valueChanges.pipe(
            filter((v) => v.chart === this.vm.chart),
            map((v) => v.loading)
        );
        this.vm.temperaturgrafLoading$ = this.highchartsLoadingObserver.valueChanges.pipe(
            filter((v) => v.chart === this.vm.tempChart),
            map((v) => v.loading)
        );
        this.vm.forbrugPanelOpenState = false;
        this.vm.panemToggleIcon = "se-mere-mitforbrug";
        this.vm.temperaturPanelButtonText = "Se mere";
        this.vm.forbrugPanelButtonText = "Se mere";
        this.vm.sideTitel = indstillinger.sideTitel;
        this.vm.tooltip = {};
        this.vm.umbracoMitForbrug = indstillinger;
        this.vm.aktuelBruger = aktuelBruger;
        this.vm.tooltip = {};
        this.vm.tempChart = null;
        this.vm.tvIndexNavn = this.vm.umbracoMitForbrug.forbrugsgraf.taellevaerkKnap.tekst1 === "Volumen" ? (this.vm.tvIndexNavn = "M3") : (this.vm.tvIndexNavn = "ENG1");
        this.vm.chart = null;
        this.vm.aFOFTileList = [];
        this.vm.monthList = this.forbrugUtils.getMonthList();
        this.vm.barColors = ["#89BCEE", "#877D7D", "#8DD98D"];
        this.vm.barColorsDarker = ["#425C97", "#2B2726", "#3E7C3E"];
        this.vm.installationCredentials = installationCredentials;
        this.vm.aktuelForbruger = { ...aktuelForbruger, indflyttet: aktuelForbruger.indflyttet || new Date(1980, 0, 1) };

        this.vm.aarsmaerke = 0;
        this.vm.loading = true;
        this.vm.fileUrl = "";
        this.vm.fejlDataTekst = "";
        this.vm.fejlDataEmail = "";
        this.vm.controls = true;
        this.vm.startMaaned = "";
        this.vm.selectedInterval = this.vm.umbracoMitForbrug.forbrugsgraf.defaultFane;
        this.vm.tempSelectedInterval = this.vm.umbracoMitForbrug.temperaturgraf.defaultFane;

        this.vm.sammenlign = 0;
        this.vm.enableYAxisData = window.innerWidth > 1000;
        this.vm.noData = false;
        this.vm.showSammenlign = false;

        this.vm.beskrivOverskrift = this.vm.umbracoMitForbrug.temperaturgraf.tekst.titel;
        this.vm.tempGrafBeskriv = this.vm.umbracoMitForbrug.temperaturgraf.tekst.tekst;
        this.vm.hideTempBeskriv = !this.vm.umbracoMitForbrug.temperaturgraf.tekst.titel;

        this.vm.eng1Caption = this.vm.umbracoMitForbrug.forbrugsgraf.taellevaerkKnap.tekst1 || "Energi";
        this.vm.m3Caption = this.vm.umbracoMitForbrug.forbrugsgraf.taellevaerkKnap.tekst2 || "Volumen";

        this.vm.unitBtn = this.vm.eng1Caption;
        this.vm.casheDataElement = {
            id: 0,
            tempFetch: false,
            engFetch: false,
            sammenlign: false,
            data: null
        };

        this.vm.tempDrillDownPress = false;

        this.vm.forbrugsParams = {
            aCallKey: 0,
            ForbrugsAfgraensning_FraAflaesning: "0",
            ForbrugsAfgraensning_TilAflaesning: "2",
            Ejendomnr: this.vm.installationCredentials.ejendomNr,
            ForbrugsAfgraensning_FraMellNr: "0",
            ForbrugsAfgraensning_TilJournalNr: "0",
            BestemtEnhed: "0",
            Godkendelser: "0",
            RadEksponent2: "0",
            RadEksponent1: "0",
            AarsMaerke: 0,
            ForbrugsAfgraensning_TilDato: "",
            Optioner: "foSkabDetaljer, foBestemtBeboer, foMedtagWebAflaes",
            ForbrugsAfgraensning_TilAfl_nr: "0",
            Belastningsfaktor: "0",
            ForbrugsAfgraensning_FraDato: "",
            AHoejDetail: false,
            AktivNr: this.vm.installationCredentials.aktivNr,
            ForbrugsAfgraensning_FraAfl_nr: "0",
            I_Nr: this.vm.installationCredentials.installationNr,
            ForbrugsAfgraensning_TilMellnr: "0",
            Aflaesningsfilter: "afMaanedsvis",
            ForbrugsAfgraensning_MedtagMellemliggendeMellemaflas: true,
            SletFiltreredeAflaesninger: true,
            AflaesningsUdjaevning: true,
            MedForventetForbrug: true,
            AflaesningsFilterDag: "ULTIMO",
            OmregnForbrugTilAktuelleEnhed: true
        };

        this.vm.options = {
            chart: {
                renderTo: "forbrug-chart"
            },
            lang: {
                prev: "Tilbage",
                next: "Frem"
            },

            exporting: {
                allowHTML: true,
                buttons: {
                    contextButton: {
                        // hvis true vil chart.exportSVGElements[0].hide() ikke helt virke frem og tilbage vil have index 2 og 4
                        enabled: false
                    },
                    prevButton: {
                        enabled: true,
                        symbol: "url(../../assets/images/left.png)",
                        // text: "<span style=\"color:orange;font-size:24px;font-weight: bold;\"></span>",
                        _titleKey: "prev",
                        align: "center",
                        x: -100,
                        y: -9,
                        height: 33,
                        width: 30,
                        symbolY: 25,
                        symbolX: 20,
                        theme: {
                            "stroke-width": 0,
                            r: 0,
                            fill: "white",
                            states: {
                                hover: {
                                    "stroke-width": 1,
                                    stroke: "silver",
                                    r: 7,
                                    fill: "white"
                                }
                            }
                        },
                        onclick: function (this: ForbrugComponent): void {
                            // Hvis bruger klikker på tilbage-pilen udføres denne kodelogik
                            if (this.vm.selectedInterval !== 3) {
                                // chart.topLevel = true;
                            }
                            if (this.vm.selectedInterval === 0) {
                                if (this.vm.selectedMonth.id === parseInt(this.vm.aarsmaerkeStart.split("-")[1], 10)) {
                                    this.vm.selectedYear--;
                                }
                                this.vm.selectedMonth.id--;
                                if (this.vm.selectedMonth.id < 1) {
                                    this.vm.selectedMonth.id = 12;
                                    this.vm.selectedMonth.year--;
                                }
                                this.reloadForbrugChart();
                            } else if (this.vm.selectedInterval === 1) {
                                if (this.forbrugUtils.getFirstShownYear(this.vm) < this.vm.selectedYear) {
                                    this.vm.selectedYear--;
                                    this.reloadForbrugChart();
                                }
                            } else {
                                if (this.forbrugUtils.getFirstShownYear(this.vm) < this.vm.selectedYear - 4) {
                                    this.vm.selectedYear--;
                                    this.reloadForbrugChart();
                                } else {
                                    // alert('nope');
                                }
                            }
                        }.bind(this)
                    },
                    nextButton: {
                        enabled: true,
                        symbol: "url(../../assets/images/right.png)",
                        _titleKey: "next",
                        align: "center",
                        x: 100,
                        y: -9,
                        height: 33,
                        width: 30,
                        symbolY: 25,
                        symbolX: 20,
                        theme: {
                            "stroke-width": 0,
                            stroke: "silver",
                            r: 0,
                            fill: "white ",
                            states: {
                                hover: {
                                    "stroke-width": 1,
                                    stroke: "silver",
                                    r: 4,
                                    fill: "white"
                                }
                            }
                        },
                        onclick: function (this: ForbrugComponent): void {
                            // Hvis bruger klikker på næste-pilen udføres denne kodelogik
                            // TODO NIM: Kunne vi lave selectedYear og selectedMonth om til et enkelt dato-felt?
                            // TODO NIM: Vi vil så kunne kalde addYear() og addMonth() som selv sørger for at rulle til næste år.
                            if (this.vm.selectedInterval === 0) {
                                this.vm.selectedMonth.id++;
                                if (this.vm.selectedMonth.id === parseInt(this.vm.aarsmaerkeStart.split("-")[1], 10)) {
                                    this.vm.selectedYear++;
                                }
                                if (this.vm.selectedMonth.id > 12) {
                                    this.vm.selectedMonth.id = 1;
                                    this.vm.selectedMonth.year++;
                                }
                            } else {
                                this.vm.selectedYear++;
                            }
                            this.reloadForbrugChart();
                        }.bind(this)
                    }
                }
            },
            credits: {
                enabled: false
            },
            title: {
                text: ""
            },
            subtitle: {
                text: this.vm.umbracoMitForbrug.forbrugsgraf.undertitel || ""
            },
            legend: {
                enabled: false,
                useHTML: true
            },
            xAxis: {
                // tickmarkPlacement: 'on',
                type: "datetime",
                dateTimeLabelFormats: {
                    day: "%e"
                }
            },
            yAxis: {
                title: {
                    align: "high",
                    offset: this.vm.isMobile ? -18 : 18,
                    rotation: 0,
                    y: -15,
                    text: "",
                    enabled: false
                },
                labels: {
                    enabled: false // !deviceDetector.isMobile()
                }
            },
            tooltip: {
                useHTML: true,
                enabled: !this.vm.isMobile,
                valueSuffix: " NaN",
                valueDecimals: 2,
                formatter: function (this: ForbrugComponent): string {
                    const hoverPoints = this.hentForbrugsgrafHoverPoints(this.vm);
                    const title = hoverPoints.title;
                    const content = this.genererTooltipContent(hoverPoints);
                    return `<b>${title}</b>${content}`;
                }.bind(this),
                shared: true
            },
            plotOptions: {
                column: {
                    stacking: "normal"
                },
                series: {
                    cursor: "pointer",
                    point: {
                        events: {
                            dblclick: function (this: ForbrugComponent): void {
                                // TODO AJH $("#report").html("dbclick on xAxis label");
                            }.bind(this),
                            click: function (this: ForbrugComponent, event: any): void {
                                const date = new Date(event.point.x);
                                if (this.vm.isMobile) {
                                    this.visForbrugsgrafTooltipDialog(date);
                                } else {
                                    this.drillDownChart(date);
                                }
                            }.bind(this)
                        }
                    }
                }
            }
        };
        this.vm.maaltSerie = {
            type: "column",
            name: "Målt",
            id: "maalt",
            mintext: "5 års graf",
            data: [],
            index: 0,
            colors: [],
            fiktiv: [],
            color: getOptions().colors[0],
            stack: "aktuel",
            states: { inactive: { enabled: false } }
        };
        this.vm.beregnetSerie = {
            type: "column",
            name: "Beregnet",
            id: "beregnet",
            mintext: "5 års graf",
            data: [],
            index: 0,
            colors: [],
            fiktiv: [],
            // colorByPoint: true,
            color: this.vm.barColorsDarker[0],
            stack: "aktuel",
            states: { inactive: { enabled: false } }
        };
        this.vm.sammenlignSerie = {
            type: "column",
            name: "sammenlign",
            mintext: "sammenlign",
            id: "sammenlign",
            data: [],
            index: 2,
            colors: [],
            fiktiv: [],
            // colorByPoint: true
            color: getOptions().colors[1],
            stack: "sammenlign"
        };
        this.vm.sammenlignSerie2 = {
            type: "column",
            name: "sammenlign2",
            mintext: "-2",
            id: "sammenlign2",
            data: [],
            index: 3,
            colors: [],
            fiktiv: [],
            // colorByPoint: true,
            color: getOptions().colors[2],
            stack: "sammenlign2"
        };
        this.vm.forventetSerie = {
            type: "spline",
            name: "Forventet",
            id: "forventet",
            data: [],
            index: 1,
            color: getOptions().colors[3],
            marker: {
                lineWidth: 2,
                lineColor: getOptions().colors[3],
                fillColor: "white"
            }
        };
        this.vm.forventetDaySerie = {
            type: "spline",
            name: "Forventet",
            id: "forventetDay",
            data: [],
            index: 1,
            color: getOptions().colors[3],
            marker: {
                lineWidth: 2,
                lineColor: getOptions().colors[3],
                fillColor: "white"
            }
        };

        this.vm.tempOptions = {
            chart: {
                renderTo: "temperatur-chart",
                defaultSeriesType: "line"
            },
            time: {
                useUTC: false
            },
            lang: {
                prev: "Tilbage",
                next: "Frem"
            },
            exporting: {
                buttons: {
                    contextButton: {
                        enabled: false
                    },
                    prevButton: {
                        enabled: true,
                        symbol: "url(../../assets/images/left.png)",
                        _titleKey: "prev",
                        align: "center",
                        x: -100,
                        y: -9,
                        height: 33,
                        width: 30,
                        symbolY: 25,
                        symbolX: 20,
                        theme: {
                            "stroke-width": 0,
                            stroke: "silver",
                            r: 0,
                            fill: "white ",
                            states: {
                                hover: {
                                    "stroke-width": 1,
                                    stroke: "silver",
                                    r: 4,
                                    fill: "white"
                                }
                            }
                        },
                        onclick: function (this: ForbrugComponent): void {
                            this.vm.tempDrillDownPress = false;

                            if (this.vm.tempSelectedInterval === 0) {
                                if (this.vm.tempSelectedMonth.id === parseInt(this.vm.aarsmaerkeStart.split("-")[1], 10)) {
                                    this.vm.tempSelectedYear--;
                                }
                                this.vm.tempSelectedMonth.id--;
                                if (this.vm.tempSelectedMonth.id < 1) {
                                    this.vm.tempSelectedMonth.id = 12;
                                    this.vm.tempSelectedMonth.year--;
                                }
                                this.reloadTemperaturChart();
                            } else if (this.vm.tempSelectedInterval === 1) {
                                if (this.forbrugUtils.getFirstShownTemperatureYear(this.vm) < this.vm.tempSelectedYear) {
                                    this.vm.tempSelectedYear--;
                                    this.reloadTemperaturChart();
                                } else {
                                    alert("nope");
                                }
                            } else {
                                if (this.forbrugUtils.getFirstShownTemperatureYear(this.vm) < this.vm.tempSelectedYear - 4) {
                                    this.vm.tempSelectedYear--;
                                    this.reloadTemperaturChart();
                                } else {
                                    alert("nope");
                                }
                            }
                        }.bind(this)
                    },
                    nextButton: {
                        enabled: true,
                        symbol: "url(../../assets/images/right.png)",
                        _titleKey: "next",
                        align: "center",
                        x: 100,
                        y: -9,
                        height: 33,
                        width: 30,
                        symbolY: 25,
                        symbolX: 20,
                        theme: {
                            "stroke-width": 0,
                            stroke: "silver",
                            r: 0,
                            fill: "white ",
                            states: {
                                hover: {
                                    "stroke-width": 1,
                                    stroke: "silver",
                                    r: 4,
                                    fill: "white"
                                }
                            }
                        },
                        onclick: function (this: ForbrugComponent): void {
                            this.vm.tempDrillDownPress = false;

                            if (this.vm.tempSelectedInterval === 0) {
                                this.vm.tempSelectedMonth.id++;
                                if (this.vm.tempSelectedMonth.id === parseInt(this.vm.aarsmaerkeStart.split("-")[1], 10)) {
                                    this.vm.tempSelectedYear++;
                                }
                                if (this.vm.tempSelectedMonth.id > 12) {
                                    this.vm.tempSelectedMonth.id = 1;
                                    this.vm.tempSelectedMonth.year++;
                                }
                            } else {
                                this.vm.tempSelectedYear++;
                            }
                            this.reloadTemperaturChart();
                        }.bind(this)
                    }
                }
            },
            title: {
                text: ""
            },
            legend: {
                enabled: false,
                useHTML: true
                // symbolHeight: "0"
            },
            credits: {
                enabled: false
            },
            subtitle: {
                text: ""
            },
            xAxis: {
                type: "datetime",
                dateTimeLabelFormats: {
                    day: "%e"
                },
                labels: {
                    textalign: "right"
                }
            },
            yAxis: {
                title: {
                    align: "high",
                    offset: 18,
                    rotation: 0,
                    y: -15,
                    text: "°C",
                    enabled: false
                },
                labels: {
                    enabled: false
                },
                startOnTick: false
            },
            tooltip: {
                useHTML: true,
                crosshairs: true,
                enabled: !this.vm.isMobile,
                valueSuffix: "°C",
                formatter: function (this: ForbrugComponent): string {
                    const hoverPoints = this.hentTemperaturgrafHoverPoints(this.vm);
                    const title = hoverPoints.title;
                    const content = this.genererTooltipContent(hoverPoints);
                    return `<b>${title}</b>${content}`;
                }.bind(this),
                shared: true
            },
            plotOptions: {
                series: {
                    point: {
                        events: {
                            click: function (this: ForbrugComponent, event: any): void {
                                const date = new Date(event.point.x);
                                if (this.vm.isMobile) {
                                    this.visTemperaturgrafTooltipDialog(date);
                                } else {
                                    this.forbrugUtils.drillDownTemperatureChart(this.vm, date);
                                }
                            }.bind(this),
                            mouseOver: function (this: ForbrugComponent, event: any): void {
                                // Tegn grå baggrund for den periode i musen hover over i temperaturgrafen.
                                const currX = event.target.x;
                                const allX = event.target.series.data.map((d) => d.x);
                                const prevX = Math.max(...allX.filter((x) => x < currX));
                                const nextX = Math.min(...allX.filter((x) => x > currX));
                                const delta = (isFinite(prevX) ? currX - prevX : isFinite(nextX) ? nextX - currX : 0) / 2;
                                this.vm.tempChart.xAxis[0].update({
                                    plotBands: [
                                        {
                                            color: "#EDEAE0",
                                            from: currX - delta,
                                            to: currX + delta
                                        }
                                    ]
                                });
                            }.bind(this)
                        }
                    },
                    marker: {
                        states: {
                            hover: {
                                radiusPlus: 1,
                                lineWidthPlus: 2
                            }
                        }
                    },
                    states: {
                        hover: {
                            lineWidthPlus: 2
                        }
                    }
                }
            },

            series: [
                {
                    name: "Afkøling",
                    id: "afkoel",
                    // step: true,
                    data: [],
                    color: getOptions().colors[4],
                    lineWidth: 4,
                    marker: {
                        lineWidth: 3,
                        radius: 5
                    }
                },
                {
                    name: "Forventet retur",
                    id: "forventetRetur",
                    data: [],
                    enableTabbing: true,
                    color: "#ABA53F",
                    lineWidth: 3,
                    marker: {
                        lineWidth: 3,
                        radius: 5
                    }
                },
                {
                    name: "Neutral",
                    id: "neutral",
                    data: [],
                    type: "arearange",
                    lineWidth: 0,
                    color: getOptions().colors[1],
                    fillOpacity: 0.3,
                    zIndex: 0,
                    enableMouseTracking: true,
                    marker: {
                        enabled: false
                    }
                },
                {
                    // step: true,
                    name: "Fremløb",
                    id: "fremlob",
                    data: [],
                    color: getOptions().colors[3],
                    lineWidth: 4,
                    marker: {
                        lineWidth: 3,
                        radius: 6
                    }
                },
                {
                    name: "Retur",
                    id: "retur",
                    data: [],
                    // step: true,
                    color: getOptions().colors[0],
                    lineWidth: 4,
                    marker: {
                        lineWidth: 3,
                        radius: 6
                    }
                }
            ]
        };

        this.vm.chartOptions = {
            lang: {
                numericSymbols: ["t", "M"],
                months: ["Januar", "Februar", "Marts", "April", "Maj", "Juni", "Juli", "August", "September", "Oktober", "November", "December"],
                weekdays: ["Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag", "Søndag"],
                shortMonths: ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"],
                loading: "",
                noData: "",
                decimalPoint: ",",
                thousandsSep: ".",
                drillUpText: "" // '<< Tilbage til {series.mintext}'
            }
        };

        this.vm.optionSelected = this.vm.tvIndexNavn;
        this.init();
    }

    @HostListener("keydown", ["$event"])
    onKeydown(event: KeyboardEvent) {
        const toggleGroup = document.querySelectorAll(".grid");
        toggleGroup.forEach((group) => {
            if (group && group.contains(document.activeElement) && (event.key === "ArrowRight" || event.key === "ArrowLeft")) {
                event.preventDefault();
                const focusableElements = group.querySelectorAll("mat-button-toggle:not([disabled]) button");
                const currentIndex = Array.from(focusableElements).indexOf(document.activeElement);
                if (event.key === "ArrowRight") {
                    const nextIndex = (currentIndex + 1) % focusableElements.length;
                    (focusableElements[nextIndex] as HTMLElement).focus();
                } else if (event.key === "ArrowLeft") {
                    const previousIndex = (currentIndex - 1 + focusableElements.length) % focusableElements.length;
                    (focusableElements[previousIndex] as HTMLElement).focus();
                }
            }
        });
    }

    public onForbrugTaellevaerkChange(unit: MatSelectChange): void {
        this.vm.tvIndexNavn = unit.value;
        this.vm.unitBtn = unit.value === "M3" ? this.vm.m3Caption : this.vm.eng1Caption;
        this.reloadForbrugChart();
    }

    public onForbrugSelectedIntervalChange(event: MatButtonToggleChange): void {
        this.vm.selectedInterval = event.value;
        this.vm.sammenlign = 0;
        this.reloadForbrugChart();
    }

    public onTemperaturSelectedIntervalChange(event: MatButtonToggleChange): void {
        this.vm.tempSelectedInterval = event.value;
        this.reloadTemperaturChart();
    }

    public sammenlignFunk(): void {
        this.vm.sammenlign = this.vm.sammenlign === 2 ? 0 : 2;
        this.reloadForbrugChart();
    }

    public onTileClick(tile: Tile): void {
        if (tile.type === "Budget") {
            if (!this.vm.forbrugPanelOpenState) {
                this.toggleForbrugPanel();
            }
        } else if (tile.type === "Temperatur") {
            if (!this.vm.temperaturPanelOpenState) {
                this.toggleTemperaturPanel();
            }
        }
        this.forbrugUtils.tileClick(this.vm, tile);
    }

    public async initForbrugChart(): Promise<void> {
        const vm = this.vm;

        // Vent på at graf-elementet findes i DOM.
        await this.waitForElementEksistererIDom("forbrug-chart");
        vm.chart = new Chart(vm.options);
        await this.waitFor(() => vm.chart.exportSVGElements);
    }

    public async initTemperaturChart(): Promise<void> {
        const vm = this.vm;
        const synlighed = this.vm.umbracoMitForbrug.temperaturgraf.synligeData;

        // TODO NIM: Vil det ikke være smartere med separate options så vi ikke skal mutere?
        if (!synlighed.retur) {
            vm.tempOptions.series.splice(4, 1);
        }
        if (!synlighed.fremloeb) {
            vm.tempOptions.series.splice(3, 1);
        }
        if (!synlighed.neutral) {
            vm.tempOptions.series.splice(2, 1);
        }
        if (!synlighed.forventetRetur) {
            vm.tempOptions.series.splice(1, 1);
        }
        if (!synlighed.afkoeling) {
            vm.tempOptions.series.splice(0, 1);
        }

        await this.waitForElementEksistererIDom("temperatur-chart");
        vm.tempChart = new Chart(vm.tempOptions);
        vm.tempChart.numberOfSeries = vm.tempChart.series.length;
        await this.waitFor(() => vm.tempChart.exportSVGElements);
    }

    public async reloadForbrugChart(): Promise<void> {
        const vm = this.vm;

        // Det er kun muligt at sammenligne med tidligere år på månedsniveau
        vm.showSammenlign = vm.selectedInterval === 1 && vm.umbracoMitForbrug.forbrugsgraf.faner.maanedSammenlign;

        // Udfyld månedsliste
        if (vm.selectedInterval !== 0 || vm.selectedMonth === undefined) {
            const mList = this.forbrugUtils.createMonthList(vm, vm.aarsmaerkeStart, vm.selectedYear);
            // vm.months = mList.monthList;
            vm.selectedMonth = mList.selectedMonth;
        }

        await this.waitForElementEksistererIDom("forbrug-chart");

        // Tegn grafen
        const taellevaerker = await this.forbrugUtils.reloadForbrugChart(vm);

        // Vis/skjul typer energi man kan vælge imellem
        vm.eng1Visibility = !!taellevaerker.find((tv) => tv === "ENG1");
        vm.m3Visibility = !!taellevaerker.find((tv) => tv === "M3");
        if (!vm.eng1Visibility && vm.m3Visibility) {
            this.vm.tvIndexNavn = "M3";
        }
        this.vm.optionSelected = this.vm.tvIndexNavn;
    }

    public async reloadTemperaturChart(): Promise<void> {
        const vm = this.vm;

        // Udfyld månedsliste
        if (vm.tempSelectedInterval !== 0 || vm.tempSelectedMonth === undefined) {
            const mList = this.forbrugUtils.createMonthList(vm, vm.aarsmaerkeStart, vm.tempSelectedYear);
            // vm.tempMonths = mList.monthList;
            vm.tempSelectedMonth = mList.selectedMonth;
        }

        await this.waitForElementEksistererIDom("temperatur-chart");

        // Tegn grafen
        await this.forbrugUtils.reloadTempChart(vm);
    }

    public drillDownChart(date: Date): void {
        const synligeFaner = this.vm.umbracoMitForbrug.forbrugsgraf.faner;
        const vm = this.vm;
        if (vm.selectedInterval === 3 && synligeFaner.maaned) {
            vm.selectedYear = date.getFullYear();
            vm.selectedInterval = 1;
            this.reloadForbrugChart();
        } else if (vm.selectedInterval === 1 && vm.sammenlign === 0 && synligeFaner.dag) {
            vm.selectedMonth.id = date.getMonth() + 1;
            vm.selectedMonth.year = date.getFullYear();
            vm.selectedInterval = 0;
            this.reloadForbrugChart();
        }
    }
    public toggleForbrugPanel() {
        this.vm.forbrugPanelOpenState = !this.vm.forbrugPanelOpenState;
        this.vm.forbrugPanelButtonText = this.vm.forbrugPanelOpenState ? "Se mindre" : "Se mere";
        this.vm.panemToggleIcon = this.vm.forbrugPanelOpenState ? "se-mindre-mitforbrug" : "se-mere-mitforbrug";
        this.toggleChartVisibles(this.vm.forbrugPanelOpenState);
    }
    public toggleTemperaturPanel() {
        this.vm.temperaturPanelOpenState = !this.vm.temperaturPanelOpenState;
        this.vm.temperaturPanelButtonText = this.vm.temperaturPanelOpenState ? "Se mindre" : "Se mere";
        this.toggleChartVisibles(this.vm.temperaturPanelOpenState, true);
    }
    toggleChartVisibles(disnabled: boolean, temperaturChart = false) {
        const chart = temperaturChart ? this.vm.tempChart : this.vm.chart;
        chart.update({
            legend: {
                enabled: disnabled
            },
            yAxis: {
                labels: {
                    enabled: disnabled
                },
                title: {
                    enabled: disnabled
                }
            },
            chart: {
                height: disnabled ? 400 : 140
            }
        });

        const chartTitle = temperaturChart ? this.vm.tempChart.chartTitle : this.vm.chart.chartTitle;
        const chartSubtitle = temperaturChart ? this.vm.tempChart.tempGrafBeskriv : this.vm.chart.chartSubTitle;
        chart.setTitle({
            text: disnabled ? chartTitle : ""
        });
        chart.setSubtitle({
            text: disnabled ? chartSubtitle : ""
        });

        dffShowHide(chart.exportSVGElements[0], disnabled ? this.vm.chart.prevButtonVisible : false);
        dffShowHide(chart.exportSVGElements[2], disnabled ? this.vm.chart.nextButtonVisible : false);
    }

    public downloadAflaesFile(): void {
        this.forbrugUtils.downloadAflaesFile(this.vm);
    }

    public async sendFejlData(): Promise<void> {
        const result = await this.sendFejlDialog.open();
        if (result) {
            this.forbrugUtils.sendFejlData(result, this.vm.selectedYear, this.vm.selectedMonth.id, this.vm.selectedInterval);
        }
    }

    private async waitForElementEksistererIDom(elementId: string): Promise<void> {
        return this.waitFor(() => !!document.getElementById(elementId));
    }

    private async waitFor(func: () => boolean): Promise<void> {
        while (!func()) {
            await new Promise((r) => setTimeout(r, 500));
        }
    }

    private visForbrugsgrafTooltipDialog(date: Date): void {
        const buttonLuk = "Luk";
        const buttonZoom = "Zoom ind";
        const canZoom = this.vm.selectedInterval !== 0 && this.vm.sammenlign === 0;
        const buttons = canZoom ? [buttonLuk, buttonZoom] : [buttonLuk];

        const hoverPoints = this.hentForbrugsgrafHoverPoints(this.vm);
        const title = hoverPoints.title;
        const innerHtml = this.genererTooltipContent(hoverPoints);
        const html = `<div class="mit-forbrug-tooltip-modal">${innerHtml}</div>`;
        const safeHtml = this.domSanitizer.bypassSecurityTrustHtml(html);

        this.simpelDialog.open(title, safeHtml, buttons).then((result) => {
            if (result === buttonZoom) {
                this.drillDownChart(date);
            }
        });
    }

    private visTemperaturgrafTooltipDialog(date: Date): void {
        const buttonLuk = "Luk";
        const buttonZoom = "Zoom ind";
        const canZoom = this.vm.tempSelectedInterval !== 0;
        const buttons = canZoom ? [buttonLuk, buttonZoom] : [buttonLuk];

        const hoverPoints = this.hentTemperaturgrafHoverPoints(this.vm);
        const title = hoverPoints.title;
        const innerHtml = this.genererTooltipContent(hoverPoints);
        const html = `<div class="mit-forbrug-tooltip-modal">${innerHtml}</div>`;
        const safeHtml = this.domSanitizer.bypassSecurityTrustHtml(html);

        this.simpelDialog.open(title, safeHtml, buttons).then((result) => {
            if (result === buttonZoom) {
                this.forbrugUtils.drillDownTemperatureChart(this.vm, date);
            }
        });
    }

    private genererTooltipContent(hoverPoints: GrafHoverPoints): string {
        const forbrugHtml = hoverPoints.points
            .filter((p) => p.forbrug)
            .map((p) => `<div><span style="color:${p.color}">\u25CF</span> ${p.forbrug.title}: ${p.forbrug.value}</div>`);
        const maalerstandHtml = hoverPoints.points.filter((p) => p.maalerstand).map((p) => `<div>${p.maalerstand.title}: ${p.maalerstand.value}</div>`);
        return [...forbrugHtml, ...maalerstandHtml].join("");
    }

    private hentForbrugsgrafHoverPoints(vm: any): GrafHoverPoints {
        // TODO NIM: Lidt funky point-klasse. Kig nærmere på den.
        const points = vm.chart.hoverPoints.map((point: { options: ForbrugChartPoint; [name: string]: any }) => {
            const color = point["color"];
            const suffix = point.options.valueSuffix;
            const digitInfo = this.forbrugUtils.getDigitInfo(suffix);
            const forbrug = formatNumber(point["y"], digitInfo);
            const maalerStand = formatNumber(point.options.maalerAflaesning, digitInfo);
            const forbrugTitle = this.hentForbrugsgrafTooltipItemTitel(point, vm.selectedInterval, vm.sammenlign);
            const maalerStandTitle =
                forbrugTitle === "Målt"
                    ? "Måleraflæsning"
                    : forbrugTitle === "Beregnet"
                      ? "Beregnet aflæsning"
                      : forbrugTitle === "Forventet"
                        ? "Forventet aflæsning"
                        : null;
            return {
                color,
                forbrug: { title: forbrugTitle, value: forbrug + suffix },
                maalerstand: maalerStandTitle && { title: maalerStandTitle, value: maalerStand + suffix }
            };
        });
        const result: GrafHoverPoints = {
            title: this.hentForbrugsgrafTooltipTitel(),
            points
        };
        return result;
    }

    private hentTemperaturgrafHoverPoints(vm: any): GrafHoverPoints {
        const points = vm.tempChart.hoverPoints.map((point) => {
            const color = point.color;
            const suffix = "°C";
            const digitInfo = this.forbrugUtils.getDigitInfo(suffix);
            const forbrugTitle = point.series.name;
            const forbrug = this.formatTooptipInfo(point, digitInfo);
            return {
                color,
                forbrug: { title: forbrugTitle, value: forbrug + suffix },
                maalerstand: null
            };
        });
        const result: GrafHoverPoints = {
            title: this.hentTemperaturgrafTooltipTitel(),
            points
        };
        return result;
    }

    private formatTooptipInfo(point: any, digitInfo: any): string {
        const forbrugTitle = point.series.name;
        if (forbrugTitle === "Neutral") {
            return formatNumber(point.low, digitInfo) + "-" + formatNumber(point.high, digitInfo);
        } else {
            return formatNumber(point.y, digitInfo);
        }
    }

    private hentForbrugsgrafTooltipItemTitel(hoverPoint: any, selectedInterval: number, sammenlign: number): string {
        const toolHeadYear = !!sammenlign ? (new Date(hoverPoint.x).getFullYear() - hoverPoint.series.index).toString() : "";
        const toolHead = hoverPoint.series.name === "Forventet" ? "Forventet" : hoverPoint.options.calculated ? "Beregnet" : "Målt";
        return trim(`${toolHeadYear} ${toolHead}`);
    }

    private hentForbrugsgrafTooltipTitel(): string {
        const hoverPoint = this.vm.chart.hoverPoint as ForbrugChartPoint;
        const date = new Date(hoverPoint.x);

        // Dag
        if (this.vm.selectedInterval === 0) {
            return formatDate(date, "Do MMMM YYYY");
        }

        // Måned
        if (this.vm.selectedInterval === 1) {
            return this.vm.sammenlign === 2 ? upperCaseFirstChar(formatDate(date, "MMMM")) : upperCaseFirstChar(formatDate(date, "MMMM YYYY"));
        }

        // År
        if (this.vm.selectedInterval === 3) {
            if (!hoverPoint || !hoverPoint.periode) {
                return "";
            }
            return hoverPoint.periode.start.getFullYear() === hoverPoint.periode.slut.getFullYear()
                ? formatDate(date, "YYYY")
                : `${formatDate(date, "YYYY")} - ${formatDate(addYears(date, 1), "YYYY")}`;
        }

        return "";
    }

    private hentTemperaturgrafTooltipTitel(): string {
        const date = new Date(this.vm.tempChart.hoverPoint.x);
        if (this.vm.tempSelectedInterval === 0) {
            return formatDayInterval(date, addDays(date, 1));
        } else if (this.vm.tempSelectedInterval === 1) {
            return upperCaseFirstChar(formatDate(date, "MMMM YYYY"));
        } else {
            return formatYearInterval(date, addYears(date, 1));
        }
    }

    private async init(): Promise<void> {
        setOptions(this.vm.chartOptions);

        this.vm.sammenlignText = "Sammenlign";
        this.vm.isMobile = this.deviceDetector.erMobilVisning();
        await this.forbrugUtils.getAarsmaerke(this.vm);

        await this.initForbrugChart();
        await this.reloadForbrugChart();

        if (this.vm.umbracoMitForbrug.temperaturgraf.synlig) {
            await this.initTemperaturChart();
            await this.reloadTemperaturChart();
        }
        if (window?.innerWidth > 768 && this.vm.umbracoMitForbrug.temperaturgraf.synlig) {
            this.toggleForbrugPanel();
            this.toggleTemperaturPanel();
        }
    }

    // Accessibility graph navigation buttons
    public prevButtonForbrug(this: ForbrugComponent): void {
        // Hvis bruger klikker på tilbage-pilen udføres denne kodelogik
        if (this.vm.selectedInterval !== 3) {
            // chart.topLevel = true;
        }
        if (this.vm.selectedInterval === 0) {
            if (this.vm.selectedMonth.id === parseInt(this.vm.aarsmaerkeStart.split("-")[1], 10)) {
                this.vm.selectedYear--;
            }
            this.vm.selectedMonth.id--;
            if (this.vm.selectedMonth.id < 1) {
                this.vm.selectedMonth.id = 12;
                this.vm.selectedMonth.year--;
            }
            this.reloadForbrugChart();
        } else if (this.vm.selectedInterval === 1) {
            if (this.forbrugUtils.getFirstShownYear(this.vm) < this.vm.selectedYear) {
                this.vm.selectedYear--;
                this.reloadForbrugChart();
            }
        } else {
            if (this.forbrugUtils.getFirstShownYear(this.vm) < this.vm.selectedYear - 4) {
                this.vm.selectedYear--;
                this.reloadForbrugChart();
            } else {
                // alert('nope');
            }
        }
    }

    public nextButtonForbrug(this: ForbrugComponent): void {
        if (this.vm.selectedInterval === 0) {
            this.vm.selectedMonth.id++;
            if (this.vm.selectedMonth.id === parseInt(this.vm.aarsmaerkeStart.split("-")[1], 10)) {
                this.vm.selectedYear++;
            }
            if (this.vm.selectedMonth.id > 12) {
                this.vm.selectedMonth.id = 1;
                this.vm.selectedMonth.year++;
            }
        } else {
            this.vm.selectedYear++;
        }
        this.reloadForbrugChart();
    }

    public prevButtonTemperatur(this: ForbrugComponent): void {
        this.vm.tempDrillDownPress = false;

        if (this.vm.tempSelectedInterval === 0) {
            if (this.vm.tempSelectedMonth.id === parseInt(this.vm.aarsmaerkeStart.split("-")[1], 10)) {
                this.vm.tempSelectedYear--;
            }
            this.vm.tempSelectedMonth.id--;
            if (this.vm.tempSelectedMonth.id < 1) {
                this.vm.tempSelectedMonth.id = 12;
                this.vm.tempSelectedMonth.year--;
            }
            this.reloadTemperaturChart();
        } else if (this.vm.tempSelectedInterval === 1) {
            if (this.forbrugUtils.getFirstShownTemperatureYear(this.vm) < this.vm.tempSelectedYear) {
                this.vm.tempSelectedYear--;
                this.reloadTemperaturChart();
            } else {
                alert("nope");
            }
        } else {
            if (this.forbrugUtils.getFirstShownTemperatureYear(this.vm) < this.vm.tempSelectedYear - 4) {
                this.vm.tempSelectedYear--;
                this.reloadTemperaturChart();
            } else {
                alert("nope");
            }
        }
    }

    public nextButtonTemperatur(this: ForbrugComponent): void {
        this.vm.tempDrillDownPress = false;

        if (this.vm.tempSelectedInterval === 0) {
            this.vm.tempSelectedMonth.id++;
            if (this.vm.tempSelectedMonth.id === parseInt(this.vm.aarsmaerkeStart.split("-")[1], 10)) {
                this.vm.tempSelectedYear++;
            }
            if (this.vm.tempSelectedMonth.id > 12) {
                this.vm.tempSelectedMonth.id = 1;
                this.vm.tempSelectedMonth.year++;
            }
        } else {
            this.vm.tempSelectedYear++;
        }
        this.reloadTemperaturChart();
    }

    public getTileLabel(tile: string): string {
        if (tile === "Vejret") {
            return "Åben vejret dialog";
        } else if (tile === "Budget") {
            return "Gå til Forbrugs graf";
        } else if (tile === "Temperatur") {
            return "Gå til Temperatur graf";
        } else if (tile === "eksternlink") {
            return "Åben link";
        }
        return "Gå til " + tile + " side";
    }
}

export interface GrafHoverPoints {
    title: string;
    points: GrafHoverPoint[];
}

export interface GrafHoverPoint {
    color: string;
    forbrug: GrafHoverPointData;
    maalerstand: GrafHoverPointData;
}

export interface GrafHoverPointData {
    title: string;
    value: string;
}
